<template>
  <div class="quote-round">
    <el-input
      v-model.trim="form.supplierName"
      placeholder="请输入投标单位名称"
      class="search-input"
      clearable
      @clear="getReviewRoundQuotePage"
      @keyup.enter.native="getReviewRoundQuotePage"
    >
      <el-button
        slot="append"
        icon="el-icon-search"
        @click="getReviewRoundQuotePage"
      />
    </el-input>

    <template v-if="roundList.length > 1">
      <div class="tab-list">
        <div
          v-for="(item, index) in roundList"
          :key="item.roundId"
          class="tab-item"
          :class="{ active: currentRound.roundId === item.roundId }"
          @click="changeTab(index)"
        >
          {{ getRoundNumLabel(item) }}
        </div>
      </div>

      <el-descriptions size="medium" class="m-t-16">
        <el-descriptions-item label="开启报价时间">
          {{ currentRound.roundStartTime || '-' }}
        </el-descriptions-item>
        <el-descriptions-item label="报价截止时间">
          {{ currentRound.roundEndTime || '-' }}
        </el-descriptions-item>
      </el-descriptions>
    </template>

    <el-scrollbar class="scrollbar-y-hidden">
      <el-table
        style="width: 99%"
        size="default"
        :data="list"
        border
        class="m-t-16"
      >
        <el-table-column
          type="index"
          label="序号"
          width="60px"
          align="center"
        />
        <el-table-column label="投标单位" align="center">
          <template v-slot:default="{ row }">
            <span>{{ row.supplierName }}</span>
          </template>
        </el-table-column>
        <el-table-column label="报价金额（元）" align="center">
          <template v-slot:default="{ row }">
            <span>{{ row.offerPrice }}</span>
            <money-in-cn :money="row.offerPrice" />
          </template>
        </el-table-column>
        <el-table-column label="企业折扣率" align="center">
          <template v-slot:default="{ row }">
            <span>{{ row.offerRate ? `${row.offerRate}%` : '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column label="折后金额（元）" align="center">
          <template v-slot:default="{ row }">
            <span>{{ row.offerRatePrice || '-' }}</span>
            <money-in-cn :money="row.offerRatePrice" />
          </template>
        </el-table-column>
        <el-table-column label="报价时间" align="center">
          <template v-slot:default="{ row }">
            <span>{{ row.offerTime }}</span>
          </template>
        </el-table-column>
        <el-table-column label="报价附件" align="center">
          <template v-slot:default="{ row }">
            <upload
              :props-data="{
                uploadMode: 'resource',
                resourceBefore: row.fileUrl,
                disabled: true,
                fileListHiddenControl: {
                  hiddenDownloadBtn: false,
                  hiddenPreviewBtn: false
                }
              }"
            />
          </template>
        </el-table-column>
      </el-table>
    </el-scrollbar>

    <pagination
      v-if="total > 0"
      :total="total"
      :page.sync="page"
      :limit.sync="size"
      @pagination="getReviewRoundQuotePage"
    />
  </div>
</template>

<script>
import MoneyInCn from '@/components/common/MoneyInCn.vue'
import Pagination from '@/components/common/Pagination.vue'
import {
  getInquiryReviewQuoteFileApi,
  getInquiryReviewQuoteRoundPageApi
} from '@/api/procure/inquiry-review'
import nzh from 'nzh/cn'
import Upload from '@/components/common/Upload/index.vue'
import { purchaseTypeDict } from '@/dictionary/project/common'
import {
  getNegotiationReviewQuoteFileApi,
  getNegotiationReviewQuoteRoundPageApi
} from '@/api/procure/negotiation-review'
import { inquiryReviewFileTypeDict } from '@/dictionary/project/inquiry'

/**
 * 轮次报价列表
 */
export default {
  name: 'OnlineReviewQuoteRoundTable',
  components: { Upload, MoneyInCn, Pagination },

  props: {
    reviewId: {
      type: [String, Number],
      default: ''
    },

    orderId: {
      type: [String, Number],
      default: ''
    },

    // 轮次列表
    roundList: {
      type: Array,
      default: () => []
    },

    purchaseType: {
      type: [Number, String],
      default: purchaseTypeDict.INQUIRY.value
    },

    // 报价文件类型
    fileType: {
      type: [String, Number],
      default: inquiryReviewFileTypeDict.OFFER_ATTACHMENT.value
    }
  },

  data() {
    return {
      roundIndex: 0,

      form: {
        supplierName: ''
      },

      list: [],
      page: 1,
      size: 10,
      total: 0
    }
  },

  computed: {
    currentRound() {
      return this.roundList[this.roundIndex] || {}
    },

    getReviewQuoteRoundPageApi() {
      const obj = {
        [purchaseTypeDict.INQUIRY.value]: getInquiryReviewQuoteRoundPageApi,
        [purchaseTypeDict.NEGOTIATION
          .value]: getNegotiationReviewQuoteRoundPageApi
      }
      return obj[this.purchaseType]
    },

    getReviewQuoteFileApi() {
      const obj = {
        [purchaseTypeDict.INQUIRY.value]: getInquiryReviewQuoteFileApi,
        [purchaseTypeDict.NEGOTIATION.value]: getNegotiationReviewQuoteFileApi
      }
      return obj[this.purchaseType]
    }
  },

  watch: {
    roundList: {
      handler(val) {
        if (val?.length > 0) {
          this.getReviewRoundQuotePage()
        }
      },
      deep: true,
      immediate: true
    }
  },

  methods: {
    getReviewRoundQuotePage() {
      const params = {
        page: this.page,
        size: this.size,
        orderId: this.orderId,
        roundId: this.currentRound.roundId,
        ...this.form
      }
      this.getReviewQuoteRoundPageApi(this.reviewId, params)
        .then(res => {
          this.total = res.data?.total || 0
          this.getQuoteFile(res.data?.records || [])
        })
        .catch(err => {
          if (err?.message) this.$message.error(err.message)
        })
    },

    getRoundNumLabel(item) {
      if (item.roundNum === 1) {
        return '首次报价'
      }
      return `${nzh.encodeS(item.roundNum)}轮报价`
    },

    changeTab(index) {
      this.roundIndex = index
      this.getReviewRoundQuotePage()
    },

    getQuoteFile(list) {
      const requestList = list.map(item => {
        const params = {
          reviewId: this.reviewId,
          orderId: this.orderId,
          supplierId: item.supplierId,
          roundId: item.roundId,
          fileType: this.fileType
        }
        return this.getReviewQuoteFileApi(params)
      })

      Promise.allSettled(requestList).then(resList => {
        this.list = list.map((item, index) => {
          item.fileUrl = resList[index]?.value?.data
          return item
        })
      })
    }
  }
}
</script>

<style scoped lang="scss">
.quote-round {
  height: 100%;
  background: $white-color;
  display: flex;
  flex-direction: column;

  .search-input {
    width: 300px;

    ::v-deep .el-input-group__append {
      background: $primary-color;
      color: $white-color;
    }
  }
}

.tab-list {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  margin-top: 16px;

  .tab-item {
    margin-top: 8px;
    min-width: 80px;
    min-height: 32px;
    padding: 0 8px;
    text-align: center;
    line-height: 32px;
    border: 1px solid $info-fourth-color;
    color: $second-text-color;
    cursor: pointer;

    &.active {
      color: $white-color;
      background: $primary-color;
      border: 1px solid $primary-color;
    }

    &:first-child {
      border-top-left-radius: 4px;
      border-bottom-left-radius: 4px;
    }

    &:last-child {
      border-top-right-radius: 4px;
      border-bottom-right-radius: 4px;
    }
  }
}
</style>
